import type { IntegrationContent } from '../../lib/integration-content-data';
import * as CryptoJS from 'crypto-js';
import type { AxiosRequestConfig } from 'axios';
import { ProductSet } from '../..';
import config from '../../config';
import { Api, ApiConfig, SKIP_WORKSPACE_ID_HEADER } from '../api';
import {
    getCurrentProductCode,
    PRODUCT_CONTEXT_HEADER,
    withPluginProductHeaders,
    withProductHeaders,
} from '../productContext';

// 定义 createSpace 的请求参数类型
export interface CreateSpaceRequest {
    title: string;
    description: string;
    userShopId: string;
}

// 定义 createSpace 的响应类型
export interface CreateSpaceResponse {
    success: boolean;
    space_id: string;
}

// 定义 updateSpace 的请求参数类型
export interface UpdateSpaceRequest {
    title?: string;
    description?: string;
    product_url?: string | null;
}

// 定义 updateSpace 的响应类型
export interface UpdateSpaceResponse {
    success: boolean;
    space_id: string;
}

// 定义 getUserSpaces 的响应类型
export interface UserSpace {
    id: string;
    created_at: string;
}

export interface GetUserSpacesResponse extends Array<UserSpace> {}

export interface GetOrdersRequest {
    /**
     * 支付状态，可选值：pending、authorized、paid、refunded等；
     * 非必填，默认查询所有支付状态。
     */
    financialStatus?: string;
    /**
     * 履约状态，可选值：unfulfilled、fulfilled、partial、restocked；
     * 非必填，默认查询所有履约状态。
     */
    fulfillmentStatus?: string;
    /**
     * 分页；
     * 非必填，默认为1
     */
    page?: string;
    /**
     * 分页数；
     * 非必填，默认为20.
     */
    size?: string;
    /**
     * user_space表的主键ID，区分归属在哪个space下的订单。
     * 非必填，默认查询所有。
     */
    spaceId?: string;
    [property: string]: any;
}


export interface GetOrdersResponse {
    data: Data;
    success: boolean;
    [property: string]: any;
}

export interface Data {
    orders: Order[];
    pagination: Pagination;
    [property: string]: any;
}

export interface Order {
    carrierName?: null;
    costPrice?: null;
    createdAt?: string;
    currency?: string;
    customer?: Customer;
    financialStatus?: string;
    fulfillmentStatus?: string;
    lastWebhookReceivedAt?: string;
    lineItems?: LineItems;
    merchantDueAmount?: null;
    orderNo?: string;
    shippingAddress?: ShippingAddress;
    shippingCost?: null;
    shopId?: string;
    shopifyOrderId?: number;
    spaceId?: string;
    totalPrice?: number;
    trackingNumber?: null;
    transactionId?: null;
    updatedAt?: string;
    userId?: string;
    webhookEvent?: string;
    [property: string]: any;
}

export interface Customer {
    admin_graphql_api_id: string;
    created_at: string;
    currency: string;
    default_address: DefaultAddress;
    email: string;
    first_name: string;
    id: number;
    last_name: string;
    multipass_identifier: null;
    note: null;
    phone: null;
    state: string;
    tax_exempt: boolean;
    tax_exemptions: string[];
    updated_at: string;
    verified_email: boolean;
    [property: string]: any;
}

export interface DefaultAddress {
    address1: string;
    address2: string;
    city: string;
    company: null;
    country: string;
    country_code: string;
    country_name: string;
    customer_id: number;
    default: boolean;
    first_name: string;
    id: number;
    last_name: string;
    name: string;
    phone: null;
    province: string;
    province_code: string;
    zip: string;
    [property: string]: any;
}

export interface LineItems {
    admin_graphql_api_id: string;
    attributed_staffs: string[];
    current_quantity: number;
    discount_allocations: string[];
    duties: string[];
    fulfillable_quantity: number;
    fulfillment_service: string;
    fulfillment_status: null;
    gift_card: boolean;
    grams: number;
    id: number;
    name: string;
    price: string;
    price_set: PriceSet;
    product_exists: boolean;
    product_id: number;
    properties: string[];
    quantity: number;
    requires_shipping: boolean;
    sales_line_item_group_id: null;
    sku: string;
    tax_lines: string[];
    taxable: boolean;
    title: string;
    total_discount: string;
    total_discount_set: TotalDiscountSet;
    variant_id: number;
    variant_inventory_management: null;
    variant_title: string;
    vendor: string;
    [property: string]: any;
}

export interface PriceSet {
    presentment_money: PriceSetPresentmentMoney;
    shop_money: PriceSetShopMoney;
    [property: string]: any;
}

export interface PriceSetPresentmentMoney {
    amount: string;
    currency_code: string;
    [property: string]: any;
}

export interface PriceSetShopMoney {
    amount: string;
    currency_code: string;
    [property: string]: any;
}

export interface TotalDiscountSet {
    presentment_money: TotalDiscountSetPresentmentMoney;
    shop_money: TotalDiscountSetShopMoney;
    [property: string]: any;
}

export interface TotalDiscountSetPresentmentMoney {
    amount: string;
    currency_code: string;
    [property: string]: any;
}

export interface TotalDiscountSetShopMoney {
    amount: string;
    currency_code: string;
    [property: string]: any;
}

export interface ShippingAddress {
    address1: string;
    address2: string;
    city: string;
    company: null;
    country: string;
    country_code: string;
    first_name: string;
    last_name: string;
    latitude: number;
    longitude: number;
    name: string;
    phone: null;
    province: string;
    province_code: string;
    zip: string;
    [property: string]: any;
}

export interface Pagination {
    hasNext: boolean;
    hasPrevious: boolean;
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    [property: string]: any;
}


// 定义 getUserSpaceDetail 的响应类型
export interface UserSpaceDetail {
    id: string;
    user_id: string;
    title: string;
    description: string;
    product_url: string | null;
    created_at: string;
    user_shop_id: string;
    preview_data: ProductSet;
    template_address: string;
    source_product_id: string;
}

export interface GetUserSpacesBatchRequest {
    /**
     * 分页页码
     * 非必填，默认为1
     */
    page?: number;
    /**
     * 分页大小
     * 非必填，默认为20
     */
    size?: number;
}

export interface GetUserSpacesBatchResponse {
    spaces: UserSpaceDetail[];
    pagination: Pagination;
}

export interface GetPulseReportsRequest {
    /**
     * Start time for the report query
     * @example "2025-09-28"
     */
    start_time?: string;
    /**
     * End time for the report query  
     * @example "2025-09-29"
     */
    end_time?: string;
    /**
     * Page number for pagination
     * @example 1
     */
    page?: number;
    /**
     * Page size for pagination
     * @example 10
     */
    size?: number;
}

export interface PulseReport {
    id: string;
    user_id: string;
    report_date: string;
    report_type: string;
    title: string;
    brief: string;
    img_url: string | null;
    oss_url: string | null;
    status: string;
    created_at: string;
    updated_at: string;
}

export interface GetPulseReportsResponse {
    data: PulseReport[];
    page: number;
    size: number;
    total: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
}


export interface UpdateOnboardingRequest {
    [key: string]: any;
}

export interface UpdateOnboardingResponse {
    success: boolean;
    message?: string;
}

export interface CreateOnboardingRequest {
    /**
     * User name
     */
    user_name: string;
    /**
     * Domain name
     */
    domain_name: string;
    /**
     * Brand name
     */
    brand_name: string;
    /**
     * Platform type
     */
    platform: string;
    /**
     * Timezone
     */
    timezone: string;
}

export interface OnboardingData {
    shop_id: string;
    pulse_id: number;
    report_utc_hour: number;
}

export interface CreateOnboardingResponse {
    success: boolean;
    message: string;
    data: OnboardingData;
}



export interface DeleteSpaceResponse {
    success: boolean;
    message?: string;
}

export interface SendUsernameRequest {
    user_name: string;
}

export interface SendUsernameResponse {
    success: boolean;
    message?: string;
}

export interface SendGenericMessageRequest {
    title: string;
    content: Record<string, string>;
}

export interface SendGenericMessageResponse {
    success: boolean;
    message?: string;
}

// ============ Workspace Members API Types ============

export interface WorkspaceMember {
    id: number | string;
    userId: string;
    workspaceId: string;
    role: string;
    membershipRole?: string | null;
    workspaceRole?: string | null;
    dataRoleId?: number | string | null;
    dataRoleName?: string | null;
    status: 'INVITED' | 'ACTIVE' | 'PENDING';
    invitedBy?: string;
    joinedAt?: string;
    createdAt: string;
    updatedAt: string;
    email: string;
    name: string;
    logoUrl?: string;
}

export interface InviteMemberRequest {
    email: string;
    role: 'OWNER' | 'ADMIN' | 'MEMBER';
    dataRoleId?: number | string | null;
    projectAccessGrants?: InviteProjectAccessGrant[];
}

export interface InviteProjectAccessGrant {
    projectId: string;
    connectionId: number | string;
    resourceType: 'CONNECTION' | 'CUSTOMER' | 'PROPERTY' | 'SITE';
    resourceKey: string;
    accessLevel: 'DISABLED' | 'READ' | 'WRITE';
}

export interface ManageableApplicationResourceProjectAccess {
    projectId: string;
    projectName: string;
    maxAccessLevel: 'DISABLED' | 'READ' | 'WRITE';
}

export interface ManageableApplicationResource {
    workspaceId: string;
    connectionId: number | string;
    provider: string;
    connectionName: string;
    connectedBy: string;
    canManageAccess: boolean;
    resourceType: 'CONNECTION' | 'CUSTOMER' | 'PROPERTY' | 'SITE';
    resourceKey: string;
    resourceName: string;
    sourceMappingType?: string | null;
    sourceMappingId?: number | string | null;
    projectAccess: ManageableApplicationResourceProjectAccess[];
}

export interface InviteMemberResponse {
    code: string;
    message: string;
    status: string;
}

export interface CancelWorkspaceInviteResponse {
    workspaceId: string;
    token: string;
    status: 'CANCELLED';
}

export interface UpdateMemberRoleRequest {
    role: 'OWNER' | 'ADMIN' | 'MEMBER';
}

export interface UpdateMemberRoleResponse {
    code: string;
    message: string;
    status: string;
}

export interface TransferWorkspaceOwnershipRequest {
    targetUserId: string;
    confirmationText: string;
}

export interface TransferWorkspaceOwnershipResponse {
    workspaceId: string;
    previousOwnerId: string;
    targetUserId: string;
    requestId: string;
    status: 'PENDING';
    expiresAt?: string;
}

export interface OwnerTransferPending {
    requestId: string;
    workspaceId: string;
    workspaceName?: string;
    previousOwnerId: string;
    previousOwnerName?: string;
    previousOwnerEmail?: string;
    targetUserId: string;
    targetName?: string;
    targetEmail?: string;
    status: 'PENDING' | 'ACCEPTED' | 'CANCELLED' | 'EXPIRED';
    valid?: boolean;
    expiresAt?: string;
    createdAt?: string;
}

export interface OwnerTransferStatusResponse extends OwnerTransferPending {
    valid: boolean;
    reasonCode?: string;
    acceptedAt?: string;
    cancelledAt?: string;
}

export interface AcceptOwnerTransferResponse {
    requestId?: string;
    workspaceId: string;
    previousOwnerId?: string;
    previousOwnerRole?: 'ADMIN';
    newOwnerId?: string;
    newOwnerRole?: 'OWNER';
    status: 'ACCEPTED';
}

export interface CancelOwnerTransferResponse {
    requestId: string;
    workspaceId: string;
    status: 'CANCELLED';
}

export interface GetWorkspaceMembersResponse {
    members: WorkspaceMember[];
    pendingInvites: PendingWorkspaceInvite[];
    entitlements?: WorkspaceMembersEntitlements;
}

export interface WorkspaceLimitEntitlement {
    configured?: boolean;
    unlimited?: boolean;
    limitValue?: number | string | null;
    source?: string;
    platformGroup?: string | null;
    billingCycle?: string | null;
    planCode?: string | null;
}

export interface WorkspaceMembersEntitlements {
    memberLimit?: WorkspaceLimitEntitlement | null;
}

export interface PendingWorkspaceInvite {
    id: string;
    email: string;
    role: 'OWNER' | 'ADMIN' | 'MEMBER';
    dataRoleId?: number | string | null;
    data_role_id?: number | string | null;
    dataRoleName?: string | null;
    data_role_name?: string | null;
    status: 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED' | 'CANCELLED';
    token: string;
    workspaceId: string;
    workspaceName: string;
    workspaceLogoUrl?: string;
    invitedBy: string;
    inviterEmail: string;
    inviterName: string;
    createdAt: string;
    updatedAt: string;
    expiresAt: string;
}

export interface RemoveMemberResponse {
    code: string;
    message: string;
    status: string;
}

// ============ Workspace Data Access API Types ============

export type WorkspaceDataAccessLevel = 'DISABLED' | 'READ' | 'WRITE';

export interface WorkspaceDataRole {
    id: number;
    workspaceId: string;
    name: string;
    description?: string | null;
    roleKey?: string | null;
    roleType: string;
    systemRole?: boolean;
    defaultRole: boolean;
    deletable: boolean;
    sortOrder?: number | null;
    status: string;
    createdFromUserId?: string | null;
    createdBy?: string | null;
    createdAt?: string;
    updatedAt?: string;
}

export interface WorkspaceRoleGrant {
    id: number;
    workspaceId: string;
    roleId: number;
    integrationId?: number | null;
    platform?: string | null;
    integrationDisplayName?: string | null;
    scopeType: string;
    resourceType?: string | null;
    resourceId?: string | null;
    accessLevel?: WorkspaceDataAccessLevel | 'NO_ACCESS' | 'ALL' | 'READ_WRITE' | null;
    permission?: string | null;
    allowed?: boolean | null;
    createdAt?: string;
    updatedAt?: string;
}

export interface WorkspaceConnectedIntegration {
    id: number;
    workspaceId: string;
    platform: string;
    slugId?: string | null;
    label?: string | null;
    displayName?: string | null;
    storeUrl?: string | null;
    externalAccountId?: string | null;
    externalAccountName?: string | null;
    externalAccountType?: string | null;
    status: string;
    connectedAt?: string;
    updatedAt?: string;
}

export interface IntegrationPermission {
    id: number;
    platform: string;
    groupKey: string;
    groupTitle: string;
    groupSortOrder?: number | null;
    permissionKey: string;
    action: 'READ' | 'WRITE' | string;
    resourceType?: string | null;
    label: string;
    description?: string | null;
    sortOrder?: number | null;
    status: string;
}

export interface SystemPermission {
    role: string;
    permission: string;
    allowed: boolean;
}

export interface WorkspaceUserRoleAssignment {
    id: number;
    workspaceId: string;
    userId: string;
    roleId: number;
    roleName: string;
    status: string;
    assignedBy?: string | null;
    assignedAt?: string;
    updatedAt?: string;
}

export interface WorkspaceDataAccessSnapshot {
    workspaceId: string;
    roles: WorkspaceDataRole[];
    userRoles: WorkspaceUserRoleAssignment[];
    grants: WorkspaceRoleGrant[];
    connections: WorkspaceConnectedIntegration[];
    integrationPermissions?: IntegrationPermission[];
    systemPermissions?: SystemPermission[];
}

export interface WorkspaceRoleGrantInput {
    integrationId?: number | null;
    accessLevel?: WorkspaceDataAccessLevel;
    scopeType?: string;
    resourceType?: string | null;
    resourceId?: string | null;
    permission?: string | null;
    allowed?: boolean | null;
}

export interface CreateWorkspaceDataRoleRequest {
    name: string;
    description?: string | null;
    createdFromUserId?: string | null;
    grants?: WorkspaceRoleGrantInput[];
}

export interface UpdateWorkspaceDataRoleRequest {
    name?: string;
    description?: string | null;
    grants?: WorkspaceRoleGrantInput[];
}

export interface UpdateWorkspaceRoleGrantsRequest {
    grants: WorkspaceRoleGrantInput[];
}

export interface AssignWorkspaceDataRoleRequest {
    roleId: number;
}

export interface DeleteWorkspaceDataRoleResponse {
    workspaceId: string;
    roleId: number;
    deleted: boolean;
}

export interface WorkspaceCapabilityResponse {
    workspaceId?: string;
    userId?: string;
    role?: string | null;
    membershipRole?: string | null;
    workspaceRoleId?: number | string | null;
    workspaceRoleKey?: string | null;
    workspaceRoleName?: string | null;
    isMember: boolean;
    isOwner: boolean;
    isAdmin: boolean;
    isAdminRole?: boolean;
    canInvite: boolean;
    canRemoveMembers: boolean;
    canChangeRoles: boolean;
    canDeleteWorkspace: boolean;
    canTransferOwnership: boolean;
    permissions?: string[];
}

export interface OutcomeLedgerSettingsResponse {
    workspaceId?: string;
    enabled?: boolean;
    dailyUpdatesEnabled?: boolean;
    daily_updates_enabled?: boolean;
    refreshHourLocal?: number | null;
    refresh_hour_local?: number | null;
    refreshMinuteLocal?: number | null;
    refresh_minute_local?: number | null;
    refreshLocalTime?: string | null;
    refresh_local_time?: string | null;
    timezone?: string | null;
    lastGeneratedAt?: string | null;
    last_generated_at?: string | null;
    privacyAcceptedAt?: string | null;
    privacy_accepted_at?: string | null;
    privacyAcceptedBy?: string | null;
    privacy_accepted_by?: string | null;
    privacyNoticeVersion?: string | null;
    privacy_notice_version?: string | null;
    updatedAt?: string | null;
    updated_at?: string | null;
}

export interface UpdateOutcomeLedgerSettingsRequest {
    enabled?: boolean;
    dailyUpdatesEnabled?: boolean | null;
    daily_updates_enabled?: boolean | null;
    refreshHourLocal?: number | null;
    refresh_hour_local?: number | null;
    refreshMinuteLocal?: number | null;
    refresh_minute_local?: number | null;
    refreshLocalTime?: string | null;
    refresh_local_time?: string | null;
    timezone?: string | null;
    acceptPrivacyNotice?: boolean;
    accept_privacy_notice?: boolean;
    privacyNoticeVersion?: string;
    privacy_notice_version?: string;
}

// Workspace Invitation Types
export interface WorkspaceInvitation {
    id: string;
    workspaceId: string;
    workspaceName: string;
    email: string;
    role: 'OWNER' | 'ADMIN' | 'MEMBER';
    dataRoleName?: string | null;
    data_role_name?: string | null;
    status: 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED';
    invitedBy: string;
    inviterEmail?: string;
    inviterName?: string;
    token: string;
    expiresAt: string;
    createdAt: string;
}

export interface GetPendingInvitationsResponse {
    code: string;
    message: string;
    data: WorkspaceInvitation[];
}

export interface AcceptInvitationResponse {
    message: string;
    workspaceId: string;
}

export interface StartInviteJoinResponse {
    action: 'REDIRECT_TO_AUTH';
    authUrl: string;
    workspaceId?: string;
    status?: 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED';
}

export interface InviteStatusResponse {
    valid: boolean;
    status?: 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED' | 'INVALID';
    workspaceId?: string;
    workspaceName?: string;
    inviterName?: string;
    inviterEmail?: string;
    invitedEmail?: string;
    role?: 'OWNER' | 'ADMIN' | 'MEMBER';
    dataRoleId?: number | string | null;
    dataRoleName?: string | null;
    data_role_name?: string | null;
    workspaceRoleKey?: string | null;
    workspaceRoleName?: string | null;
    expiresAt?: string;
    daysUntilExpiry?: number;
    expired?: boolean;
}

// ============ Workspace Creation API Types ============

export interface CreateWorkspaceRequest {
    name: string;
    logoUrl?: string;
    settings?: {
        description?: string;
        [key: string]: any;
    };
    /**
     * Timezone
     */
    timezone: string;
    referralCode?: string;
    referralAttributionToken?: string | null;
}

export interface ReferralEntryView {
    referredWorkspaceName?: string | null;
    referredCreatorEmailMasked?: string | null;
    /** Current invitee subscription state, independent of referral-credit processing. */
    referredSubscriptionStatus?: string | null;
    status?: string | null;
    credit?: ReferralCreditView | null;
    createdAt?: string | null;
}

export interface ReferralCreditView {
    status?: string | null;
    originalAmountMinor?: number | null;
    remainingAmountMinor?: number | null;
    currency?: string | null;
    earnedAt?: string | null;
    settledAt?: string | null;
}

export interface ReferralSummaryView {
    totalReferrals: number;
    availableCreditAmountMinor?: number | null;
    allocatedCreditAmountMinor?: number | null;
    /** Complete unconsumed balance, including renewal, upgrade-checkout and recovery holds. */
    remainingCreditAmountMinor?: number | null;
    /** Historical net earned total; unlike remaining balance, this includes credit already consumed. */
    totalEarnedCreditAmountMinor?: number | null;
    nextPeriodStatus?: string | null;
    nextPeriodAppliedMinor?: number | null;
    maxRewardAmountMinorPerReferral?: number | null;
    currency?: string | null;
}

export interface ReferralWorkspaceView {
    productSupported?: boolean;
    eligible: boolean;
    canViewDetails?: boolean;
    reasonCode?: string | null;
    shareUrl?: string | null;
    referrals: ReferralEntryView[];
    summary?: ReferralSummaryView | null;
}

export interface ReferralResolveView {
    accepted: boolean;
    productCode?: string | null;
    reasonCode?: string | null;
    message?: string | null;
    attributionToken?: string | null;
    inviterDisplayName?: string | null;
}

export interface UpdateWorkspaceRequest {
    name?: string;
    logoUrl?: string;
    settings?: {
        description?: string;
        [key: string]: any;
    };
}

export interface WorkspaceData {
    id: string;
    name: string;
    logoUrl: string;
    ownerId: string;
    settings: {
        description?: string;
        [key: string]: any;
    };
    status: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
    memberCount: number;
    currentUserRole?: string | null;
    membershipRole?: string | null;
    workspaceRoleId?: number | string | null;
    workspaceRoleKey?: string | null;
    workspaceRoleName?: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface CreateWorkspaceResponse {
    code: string;
    message: string;
    status: string;
    data: WorkspaceData;
}

export interface RuntimeLaunchStatusResponse {
    ready: boolean;
    status: string;
    workspaceId: string;
    billingRequired?: boolean;
    billingStatus?: string;
    connectBundleAvailable?: boolean;
    endpointReachable?: boolean;
    registeredAt?: string;
    lastHeartbeatAt?: string;
    lastHealthAt?: string;
    wsUrl?: string;
    gatewayToken?: string;
    httpBaseUrl?: string;
    workspaceHost?: string;
}

export interface CampaignVerificationCodeResponse {
    verificationCode: string;
    agentName: string;
    tweetTemplate: string;
    tweetUrl: string;
}

export interface VerifyCampaignTweetRequest {
    tweetUrl: string;
}

export interface VerifyCampaignTweetResponse {
    status: string;
    verifiedAt: string | null;
    workspaceId: string | null;
    activationStatus: string | null;
}

export interface CampaignVerificationStatusResponse {
    id: string;
    userId: string;
    workspaceId: string | null;
    verificationCode: string;
    agentName: string;
    status: string;
    tweetUrl: string | null;
    verifiedAt: string | null;
    createdAt: string;
    updatedAt: string;
}

export class FeatureService {
    private api: Api;
    private integrationPermissionsCache: IntegrationPermission[] | null = null;
    private integrationContentCache: { rows: IntegrationContent[]; expiresAt: number } | null = null;
    private integrationContentPromise: Promise<IntegrationContent[]> | null = null;
    private integrationPermissionsPromise: Promise<IntegrationPermission[]> | null = null;
    private workspaceCapabilitiesCache = new Map<string, { value: WorkspaceCapabilityResponse; expiresAt: number }>();
    private workspaceCapabilitiesPromises = new Map<string, Promise<WorkspaceCapabilityResponse>>();
    private workspaceCapabilitiesTtlMs = 30000;
    private outcomeLedgerSettingsCache = new Map<string, { value: OutcomeLedgerSettingsResponse; expiresAt: number }>();
    private outcomeLedgerSettingsPromises = new Map<string, Promise<OutcomeLedgerSettingsResponse>>();
    private outcomeLedgerSettingsTtlMs = 30000;

    constructor(config: ApiConfig) {
        this.api = new Api(config);
    }

    private withWorkspaceHeader(workspaceId?: string | null) {
        if (!workspaceId) return undefined;

        return {
            headers: {
                'X-Workspace-Id': workspaceId,
            },
        };
    }

    private withWorkspaceProductHeaders(workspaceId?: string | null) {
        return withProductHeaders(getCurrentProductCode(), this.withWorkspaceHeader(workspaceId));
    }

    /**
     * Create a new space
     * @param data - Space creation data
     * @returns Promise with space creation result
     */
    async createSpace(data: CreateSpaceRequest): Promise<CreateSpaceResponse> {
        return this.api.post<CreateSpaceResponse>('/api/shopify/createSpace', data);
    }

    /**
     * Get user spaces list
     * @param userShopId - User shop ID
     * @returns Promise with user spaces list
     */
    async getUserSpaces(userShopId: string): Promise<GetUserSpacesResponse> {
        return this.api.get<GetUserSpacesResponse>(`/api/shopify/userSpaces/${userShopId}`);
    }

    /**
     * Get specific space detail
     * @param spaceId - Space ID
     * @returns Promise with space detail
     */
    async getUserSpaceDetail(spaceId: string): Promise<UserSpaceDetail> {
        return this.api.get<UserSpaceDetail>(`/api/shopify/userSpaceDetail/${spaceId}`);
    }

    /**
     * Update space information
     * @param spaceId - Space ID
     * @param data - Space update data
     * @returns Promise with update result
     */
    async updateSpace(spaceId: string, data: UpdateSpaceRequest): Promise<UpdateSpaceResponse> {
        return this.api.put<UpdateSpaceResponse>(`/api/shopify/updateSpace/${spaceId}`, data);
    }

    /**
     * Delete space (logical deletion)
     * @param spaceId - Space ID to delete
     * @returns Promise with deletion result
     */
    async deleteSpace(spaceId: string): Promise<DeleteSpaceResponse> {
        return this.api.delete<DeleteSpaceResponse>(`/api/shopify/deleteSpace/${spaceId}`);
    }

    /**
     * Get current user's order information
     * @param params - Query parameters for filtering orders
     * @returns Promise with user orders data
     */
    async getUserOrders(params?: GetOrdersRequest): Promise<GetOrdersResponse> {
        return this.api.get<GetOrdersResponse>('/api/orders/getUserOrders', { params });
    }

    /**
     * Batch query user's space detailed information
     * @param params - Query parameters for pagination
     * @returns Promise with user spaces batch data
     */
    async getUserSpacesBatch(params?: GetUserSpacesBatchRequest): Promise<GetUserSpacesBatchResponse> {
        return this.api.get<GetUserSpacesBatchResponse>('/api/shopify/userSpacesBatch', { params });
    }

    /**
     * Get pulse reports list
     * @param params - Query parameters for filtering and pagination
     * @returns Promise with pulse reports data
     */
    async getPulseReports(params?: GetPulseReportsRequest): Promise<GetPulseReportsResponse> {
        return this.api.get<GetPulseReportsResponse>('/api/pulse/reports', { params });
    }

    /**
     * Update onboarding data
     * @param shopInfoId - Shop info ID for the update
     * @param data - Update data object
     * @returns Promise with update response
     */
    async updateOnboarding(shopInfoId: string, data: UpdateOnboardingRequest): Promise<UpdateOnboardingResponse> {
        return this.api.put<UpdateOnboardingResponse>(`/api/pulse/update/${shopInfoId}`, data);
    }

    /**
     * @deprecated
     * Create onboarding data
     * @param data - Onboarding creation data
     * @returns Promise with creation response
     */
    async createOnboarding(data: CreateOnboardingRequest): Promise<CreateOnboardingResponse> {
        return this.api.post<CreateOnboardingResponse>('/api/pulse/insert', data);
    }

    /**
     * Get pulse report detail by ID
     * @param reportId - Report ID to fetch details for
     * @returns Promise with report detail data
     */
    async getPulseReportDetail(reportId: string): Promise<PulseReport> {
        return this.api.get<PulseReport>(`/api/pulse/report/${reportId}`);
    }

    async sendUsername(data: SendUsernameRequest): Promise<SendUsernameResponse> {
        return this.api.post<SendUsernameResponse>('/api/pulse/send-username', data);
    }

    async sendGenericMessage(data: SendGenericMessageRequest): Promise<SendGenericMessageResponse> {
        // 1. 准备数据
        const secret = "super_strong_internal_secret_key";
        const requestData = {
            title: data.title,
            content: data.content
        };

        // 必须保证 JSON 序列化后的字符串与后端收到的一致
        const requestBody = JSON.stringify(requestData);

        // 获取当前时间戳（秒）
        const timestamp = Math.floor(Date.now() / 1000);

        // 2. 生成签名
        // 签名规则: HmacSHA256(timestamp + ":" + requestBody, secret)
        const messageToSign = `${timestamp}:${requestBody}`;
        const signature = CryptoJS.HmacSHA256(messageToSign, secret).toString(CryptoJS.enc.Hex);

        // 3. 发送请求，添加自定义 headers
        return this.api.post<SendGenericMessageResponse>('/api/slack/sendPublicMessage', requestData, {
            headers: {
                'X-Timestamp': timestamp.toString(),
                'Authorization': `Bearer ${signature}`
            }
        });
    }

    // ============ Workspace Members API Methods ============

    /**
     * Get all members of a workspace
     * @param workspaceId - Workspace ID
     * @returns Promise with workspace members list
     */
    async getWorkspaceMembers(workspaceId: string): Promise<GetWorkspaceMembersResponse> {
        return this.api.get<GetWorkspaceMembersResponse>(
            `/api/workspaces/${workspaceId}/members`,
            this.withWorkspaceProductHeaders(workspaceId),
        );
    }

    async getManageableApplicationResources(
        workspaceId: string
    ): Promise<ManageableApplicationResource[]> {
        return this.api.get<ManageableApplicationResource[]>(
            `/api/workspaces/${workspaceId}/resource-contract/manageable-application-resources`,
            this.withWorkspaceProductHeaders(workspaceId),
        );
    }

    /**
     * Invite a member to workspace
     * @param workspaceId - Workspace ID
     * @param data - Invitation data (email and role)
     * @returns Promise with invitation result
     */
    async inviteWorkspaceMember(
        workspaceId: string,
        data: InviteMemberRequest
    ): Promise<InviteMemberResponse> {
        return this.api.post<InviteMemberResponse>(
            `/api/workspaces/${workspaceId}/members`,
            data,
            this.withWorkspaceProductHeaders(workspaceId),
        );
    }

    async cancelWorkspaceInvite(
        workspaceId: string,
        token: string
    ): Promise<CancelWorkspaceInviteResponse> {
        return this.api.delete<CancelWorkspaceInviteResponse>(
            `/api/workspaces/${workspaceId}/invites/${encodeURIComponent(token)}`,
            this.withWorkspaceProductHeaders(workspaceId),
        );
    }

    /**
     * Remove a member from workspace
     * @param workspaceId - Workspace ID
     * @param memberId - Member ID to remove
     * @returns Promise with removal result
     */
    async removeWorkspaceMember(
        workspaceId: string,
        memberId: string
    ): Promise<RemoveMemberResponse> {
        return this.api.delete<RemoveMemberResponse>(
            `/api/workspaces/${workspaceId}/members/${memberId}`,
            this.withWorkspaceProductHeaders(workspaceId),
        );
    }

    /**
     * Update a member's role in the workspace
     * @param workspaceId - Workspace ID
     * @param memberId - Member ID to update
     * @param data - Update data (role)
     * @returns Promise with update result
     */
    async updateWorkspaceMemberRole(
        workspaceId: string,
        memberId: string,
        data: UpdateMemberRoleRequest
    ): Promise<UpdateMemberRoleResponse> {
        return this.api.put<UpdateMemberRoleResponse>(
            `/api/workspaces/${workspaceId}/members/${memberId}/role`,
            data,
            this.withWorkspaceProductHeaders(workspaceId),
        );
    }

    async transferWorkspaceOwnership(
        workspaceId: string,
        data: TransferWorkspaceOwnershipRequest
    ): Promise<TransferWorkspaceOwnershipResponse> {
        return this.api.post<TransferWorkspaceOwnershipResponse>(
            `/api/workspaces/${workspaceId}/owner-transfer`,
            data,
            this.withWorkspaceProductHeaders(workspaceId),
        );
    }

    async getPendingOwnerTransfer(workspaceId: string): Promise<OwnerTransferPending | null> {
        return this.api.get<OwnerTransferPending | null>(
            `/api/workspaces/${workspaceId}/owner-transfer/pending`,
            this.withWorkspaceProductHeaders(workspaceId),
        );
    }

    async cancelOwnerTransfer(
        workspaceId: string,
        requestId: string
    ): Promise<CancelOwnerTransferResponse> {
        return this.api.post<CancelOwnerTransferResponse>(
            `/api/workspaces/${workspaceId}/owner-transfer/cancel`,
            { requestId },
            this.withWorkspaceProductHeaders(workspaceId),
        );
    }

    async getWorkspaceDataAccess(workspaceId: string): Promise<WorkspaceDataAccessSnapshot> {
        return this.api.get<WorkspaceDataAccessSnapshot>(
            `/api/workspaces/${workspaceId}/data-access`,
            this.withWorkspaceProductHeaders(workspaceId)
        );
    }

    async getWorkspaceDataRoles(workspaceId: string): Promise<WorkspaceDataRole[]> {
        return this.api.get<WorkspaceDataRole[]>(
            `/api/workspaces/${workspaceId}/data-access/roles`,
            this.withWorkspaceProductHeaders(workspaceId)
        );
    }

    async getWorkspaceDataConnections(workspaceId: string): Promise<WorkspaceConnectedIntegration[]> {
        return this.api.get<WorkspaceConnectedIntegration[]>(
            `/api/workspaces/${workspaceId}/data-access/connections`,
            this.withWorkspaceProductHeaders(workspaceId)
        );
    }

    async getWorkspaceDataRoleGrants(
        workspaceId: string,
        roleId: number
    ): Promise<WorkspaceRoleGrant[]> {
        return this.api.get<WorkspaceRoleGrant[]>(
            `/api/workspaces/${workspaceId}/data-access/roles/${roleId}/grants`,
            this.withWorkspaceProductHeaders(workspaceId)
        );
    }

    async getIntegrationContent(options: { forceRefresh?: boolean } = {}): Promise<IntegrationContent[]> {
        if (!options.forceRefresh && this.integrationContentCache
            && this.integrationContentCache.expiresAt > Date.now()) {
            return this.integrationContentCache.rows;
        }
        if (this.integrationContentPromise) {
            return this.integrationContentPromise;
        }
        const request = this.api.get<IntegrationContent[]>('/api/workspaces/data-access/integration-content');
        this.integrationContentPromise = request;
        try {
            const rows = await request;
            if (!Array.isArray(rows)) throw new Error('Invalid integration content response');
            this.integrationContentCache = { rows, expiresAt: Date.now() + 300_000 };
            return rows;
        } finally {
            this.integrationContentPromise = null;
        }
    }

    getCachedIntegrationPermissions(): IntegrationPermission[] | null {
        return this.integrationPermissionsCache;
    }

    async getIntegrationPermissions(options: { forceRefresh?: boolean } = {}): Promise<IntegrationPermission[]> {
        if (!options.forceRefresh && this.integrationPermissionsCache) {
            return this.integrationPermissionsCache;
        }

        if (!options.forceRefresh && this.integrationPermissionsPromise) {
            return this.integrationPermissionsPromise;
        }

        this.integrationPermissionsPromise = this.api.get<IntegrationPermission[]>(
            '/api/workspaces/data-access/integration-permissions'
        )
            .then((permissions) => {
                this.integrationPermissionsCache = permissions || [];
                return this.integrationPermissionsCache;
            })
            .catch((error) => {
                this.integrationPermissionsPromise = null;
                throw error;
            });

        return this.integrationPermissionsPromise;
    }

    prefetchIntegrationPermissions(): Promise<IntegrationPermission[]> {
        return this.getIntegrationPermissions();
    }

    async createWorkspaceDataRole(
        workspaceId: string,
        data: CreateWorkspaceDataRoleRequest
    ): Promise<WorkspaceDataRole> {
        return this.api.post<WorkspaceDataRole>(
            `/api/workspaces/${workspaceId}/data-access/roles`,
            data,
            this.withWorkspaceProductHeaders(workspaceId)
        );
    }

    async updateWorkspaceDataRole(
        workspaceId: string,
        roleId: number,
        data: UpdateWorkspaceDataRoleRequest
    ): Promise<WorkspaceDataRole> {
        const role = await this.api.put<WorkspaceDataRole>(
            `/api/workspaces/${workspaceId}/data-access/roles/${roleId}`,
            data,
            this.withWorkspaceProductHeaders(workspaceId)
        );
        this.clearWorkspaceCapabilitiesCache(workspaceId);
        return role;
    }

    async deleteWorkspaceDataRole(
        workspaceId: string,
        roleId: number
    ): Promise<DeleteWorkspaceDataRoleResponse> {
        const response = await this.api.delete<DeleteWorkspaceDataRoleResponse>(
            `/api/workspaces/${workspaceId}/data-access/roles/${roleId}`,
            this.withWorkspaceProductHeaders(workspaceId)
        );
        this.clearWorkspaceCapabilitiesCache(workspaceId);
        return response;
    }

    async updateWorkspaceDataRoleGrants(
        workspaceId: string,
        roleId: number,
        data: UpdateWorkspaceRoleGrantsRequest
    ): Promise<WorkspaceRoleGrant[]> {
        const grants = await this.api.put<WorkspaceRoleGrant[]>(
            `/api/workspaces/${workspaceId}/data-access/roles/${roleId}/grants`,
            data,
            this.withWorkspaceProductHeaders(workspaceId)
        );
        this.clearWorkspaceCapabilitiesCache(workspaceId);
        return grants;
    }

    async assignWorkspaceDataRole(
        workspaceId: string,
        userId: string,
        data: AssignWorkspaceDataRoleRequest
    ): Promise<WorkspaceUserRoleAssignment> {
        const assignment = await this.api.put<WorkspaceUserRoleAssignment>(
            `/api/workspaces/${workspaceId}/data-access/members/${userId}`,
            data,
            this.withWorkspaceProductHeaders(workspaceId)
        );
        this.clearWorkspaceCapabilitiesCache(workspaceId);
        return assignment;
    }

    getCachedOutcomeLedgerSettings(workspaceId: string): OutcomeLedgerSettingsResponse | null {
        const cached = this.outcomeLedgerSettingsCache.get(workspaceId);
        if (!cached || cached.expiresAt <= Date.now()) {
            this.outcomeLedgerSettingsCache.delete(workspaceId);
            return null;
        }

        return cached.value;
    }

    async getOutcomeLedgerSettings(
        workspaceId: string,
        options: { forceRefresh?: boolean } = {}
    ): Promise<OutcomeLedgerSettingsResponse> {
        if (!options.forceRefresh) {
            const cached = this.getCachedOutcomeLedgerSettings(workspaceId);
            if (cached) {
                return cached;
            }

            const pending = this.outcomeLedgerSettingsPromises.get(workspaceId);
            if (pending) {
                return pending;
            }
        }

        let promise: Promise<OutcomeLedgerSettingsResponse>;
        promise = this.api.get<OutcomeLedgerSettingsResponse>(
            `/api/workspaces/${workspaceId}/outcome-ledger/settings`,
            this.withWorkspaceHeader(workspaceId)
        )
            .then((settings) => {
                if (this.outcomeLedgerSettingsPromises.get(workspaceId) === promise) {
                    this.outcomeLedgerSettingsCache.set(workspaceId, {
                        value: settings,
                        expiresAt: Date.now() + this.outcomeLedgerSettingsTtlMs,
                    });
                    this.outcomeLedgerSettingsPromises.delete(workspaceId);
                }
                return settings;
            })
            .catch((error) => {
                if (this.outcomeLedgerSettingsPromises.get(workspaceId) === promise) {
                    this.outcomeLedgerSettingsPromises.delete(workspaceId);
                }
                throw error;
            });

        this.outcomeLedgerSettingsPromises.set(workspaceId, promise);
        return promise;
    }

    async updateOutcomeLedgerSettings(
        workspaceId: string,
        data: UpdateOutcomeLedgerSettingsRequest
    ): Promise<OutcomeLedgerSettingsResponse> {
        const settings = await this.api.put<OutcomeLedgerSettingsResponse>(
            `/api/workspaces/${workspaceId}/outcome-ledger/settings`,
            data,
            this.withWorkspaceHeader(workspaceId)
        );
        this.outcomeLedgerSettingsCache.set(workspaceId, {
            value: settings,
            expiresAt: Date.now() + this.outcomeLedgerSettingsTtlMs,
        });
        this.outcomeLedgerSettingsPromises.delete(workspaceId);
        return settings;
    }

    clearOutcomeLedgerSettingsCache(workspaceId?: string): void {
        if (workspaceId) {
            this.outcomeLedgerSettingsCache.delete(workspaceId);
            this.outcomeLedgerSettingsPromises.delete(workspaceId);
            return;
        }

        this.outcomeLedgerSettingsCache.clear();
        this.outcomeLedgerSettingsPromises.clear();
    }

    getCachedWorkspaceCapabilities(workspaceId: string): WorkspaceCapabilityResponse | null {
        const cached = this.workspaceCapabilitiesCache.get(workspaceId);
        if (!cached || cached.expiresAt <= Date.now()) {
            this.workspaceCapabilitiesCache.delete(workspaceId);
            return null;
        }

        return cached.value;
    }

    async getWorkspaceCapabilities(
        workspaceId: string,
        options: { forceRefresh?: boolean } = {}
    ): Promise<WorkspaceCapabilityResponse> {
        if (!options.forceRefresh) {
            const cached = this.getCachedWorkspaceCapabilities(workspaceId);
            if (cached) {
                return cached;
            }

            const pending = this.workspaceCapabilitiesPromises.get(workspaceId);
            if (pending) {
                return pending;
            }
        }

        let promise: Promise<WorkspaceCapabilityResponse>;
        promise = this.api.get<WorkspaceCapabilityResponse>(
            `/api/workspaces/${workspaceId}/me/capabilities`,
            this.withWorkspaceProductHeaders(workspaceId)
        )
            .then((capabilities) => {
                if (this.workspaceCapabilitiesPromises.get(workspaceId) === promise) {
                    this.workspaceCapabilitiesCache.set(workspaceId, {
                        value: capabilities,
                        expiresAt: Date.now() + this.workspaceCapabilitiesTtlMs,
                    });
                    this.workspaceCapabilitiesPromises.delete(workspaceId);
                }
                return capabilities;
            })
            .catch((error) => {
                if (this.workspaceCapabilitiesPromises.get(workspaceId) === promise) {
                    this.workspaceCapabilitiesPromises.delete(workspaceId);
                }
                throw error;
            });

        this.workspaceCapabilitiesPromises.set(workspaceId, promise);
        return promise;
    }

    clearWorkspaceCapabilitiesCache(workspaceId?: string): void {
        if (workspaceId) {
            this.workspaceCapabilitiesCache.delete(workspaceId);
            this.workspaceCapabilitiesPromises.delete(workspaceId);
            return;
        }

        this.workspaceCapabilitiesCache.clear();
        this.workspaceCapabilitiesPromises.clear();
    }

    /**
     * Get pending workspace invitations by email
     * @param email - User email to query invitations
     * @returns Promise with pending invitations list
     */
    async getPendingInvitations(email: string): Promise<GetPendingInvitationsResponse> {
        return this.api.get<GetPendingInvitationsResponse>('/api/workspaces/invites/pending', {
            params: { email },
            ...withProductHeaders(getCurrentProductCode(), {
                headers: { [SKIP_WORKSPACE_ID_HEADER]: 'true' },
            }),
        });
    }

    /**
     * Accept a workspace invitation by token
     * @param token - Invitation token
     * @returns Promise with accept result
     */
    async acceptInvitation(token: string): Promise<AcceptInvitationResponse> {
        return this.api.post<AcceptInvitationResponse>(
            '/api/workspaces/invite/accept',
            { token },
            withProductHeaders(getCurrentProductCode(), {
                headers: { [SKIP_WORKSPACE_ID_HEADER]: 'true' },
            }),
        );
    }

    /**
     * Start invite login by token. This creates a fresh auth action link only
     * after the user clicks Join on the stable invite page.
     */
    async startInviteJoin(token: string): Promise<StartInviteJoinResponse> {
        return this.api.post<StartInviteJoinResponse>(
            '/api/workspaces/invite/join',
            { token },
            withProductHeaders(getCurrentProductCode(), {
                headers: { [SKIP_WORKSPACE_ID_HEADER]: 'true' },
            }),
        );
    }

    /**
     * Get workspace invitation status by token
     * @param token - Invitation token
     * @returns Promise with invitation status
     */
    async getInviteStatus(token: string): Promise<InviteStatusResponse> {
        return this.api.get<InviteStatusResponse>(
            '/api/workspaces/invite/status',
            {
                params: { token },
                ...withProductHeaders(getCurrentProductCode(), {
                    headers: { [SKIP_WORKSPACE_ID_HEADER]: 'true' },
                }),
            }
        );
    }

    async getOwnerTransferStatus(token: string): Promise<OwnerTransferStatusResponse> {
        return this.api.get<OwnerTransferStatusResponse>(
            '/api/workspaces/owner-transfer/status',
            withProductHeaders(getCurrentProductCode(), {
                params: { token },
                headers: { [SKIP_WORKSPACE_ID_HEADER]: 'true' },
            }),
        );
    }

    async acceptOwnerTransfer(token: string): Promise<AcceptOwnerTransferResponse> {
        return this.api.post<AcceptOwnerTransferResponse>(
            '/api/workspaces/owner-transfer/accept',
            { token },
            withProductHeaders(getCurrentProductCode(), {
                headers: { [SKIP_WORKSPACE_ID_HEADER]: 'true' },
            }),
        );
    }

    // ============ Workspace Creation API Methods ============

    /**
     * Create a new workspace
     * @param data - Workspace creation data (name, optional logoUrl and settings)
     * @returns Promise with created workspace data (response.data.data is unwrapped by Api.request)
     */
    async createWorkspace(data: CreateWorkspaceRequest, axiosRequestConfig?: AxiosRequestConfig): Promise<WorkspaceData> {
        return this.api.post<WorkspaceData>('/api/workspaces', data, axiosRequestConfig);
    }

    async ensureOnboardingWorkspace(data: CreateWorkspaceRequest, axiosRequestConfig?: AxiosRequestConfig): Promise<WorkspaceData> {
        return this.api.post<WorkspaceData>('/api/workspaces/onboarding/ensure', data, axiosRequestConfig);
    }

    async getWorkspaceReferral(workspaceId: string): Promise<ReferralWorkspaceView> {
        return this.api.get<ReferralWorkspaceView>(`/api/workspaces/${workspaceId}/referral`);
    }

    async createWorkspaceReferralCode(workspaceId: string, idempotencyKey: string): Promise<ReferralWorkspaceView> {
        return this.api.post<ReferralWorkspaceView>(
            `/api/workspaces/${workspaceId}/referral/code`,
            {},
            { headers: { 'Idempotency-Key': idempotencyKey } },
        );
    }

    async rotateWorkspaceReferralCode(workspaceId: string, idempotencyKey: string): Promise<ReferralWorkspaceView> {
        return this.api.post<ReferralWorkspaceView>(
            `/api/workspaces/${workspaceId}/referral/code/rotate`,
            { confirmed: true },
            { headers: { 'Idempotency-Key': idempotencyKey } },
        );
    }

    async createPluginWorkspace(data: CreateWorkspaceRequest, idempotencyKey?: string): Promise<WorkspaceData> {
        return this.api.post<WorkspaceData>(
            '/api/products/plugin/workspaces',
            data,
            withPluginProductHeaders({
                headers: {
                    [SKIP_WORKSPACE_ID_HEADER]: 'true',
                    ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
                },
            }),
        );
    }

    async ensurePluginOnboardingWorkspace(data: CreateWorkspaceRequest, idempotencyKey?: string): Promise<WorkspaceData> {
        return this.api.post<WorkspaceData>(
            '/api/products/plugin/workspaces/onboarding/ensure',
            data,
            withPluginProductHeaders({
                headers: {
                    [SKIP_WORKSPACE_ID_HEADER]: 'true',
                    ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
                },
            }),
        );
    }

    async updateWorkspace(workspaceId: string, data: UpdateWorkspaceRequest, axiosRequestConfig?: AxiosRequestConfig): Promise<WorkspaceData> {
        return this.api.put<WorkspaceData>(
            `/api/workspaces/${workspaceId}`,
            data,
            axiosRequestConfig?.headers?.[PRODUCT_CONTEXT_HEADER]
                ? axiosRequestConfig
                : withProductHeaders(getCurrentProductCode(), axiosRequestConfig),
        );
    }

    async getWorkspaceRuntimeLaunchStatus(workspaceId: string): Promise<RuntimeLaunchStatusResponse> {
        return this.api.get<RuntimeLaunchStatusResponse>(`/api/workspaces/${workspaceId}/runtime-launch`);
    }

    async ensureWorkspaceRuntimeLaunch(workspaceId: string): Promise<RuntimeLaunchStatusResponse> {
        return this.api.post<RuntimeLaunchStatusResponse>(`/api/workspaces/${workspaceId}/runtime-launch/ensure`);
    }

    async generateCampaignVerificationCode(workspaceId?: string | null): Promise<CampaignVerificationCodeResponse> {
        return this.api.post<CampaignVerificationCodeResponse>(
            '/api/campaign/verification-code',
            undefined,
            this.withWorkspaceHeader(workspaceId),
        );
    }

    async verifyCampaignTweet(
        data: VerifyCampaignTweetRequest,
        workspaceId?: string | null,
    ): Promise<VerifyCampaignTweetResponse> {
        return this.api.post<VerifyCampaignTweetResponse>(
            '/api/campaign/verify-tweet',
            data,
            this.withWorkspaceHeader(workspaceId),
        );
    }

    async getCampaignVerificationStatus(workspaceId?: string | null): Promise<CampaignVerificationStatusResponse> {
        return this.api.get<CampaignVerificationStatusResponse>(
            '/api/campaign/verification-status',
            this.withWorkspaceHeader(workspaceId),
        );
    }
}

export const featureService = new FeatureService({
    baseURL: config.services.feature.apiHost,
    headers: {'Content-Type': 'application/json'}
});
